import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDropzone } from "react-dropzone";
import { IVidro } from "../types/vidro.types";
import { uploadImagem, removerImagem, definirNomeMidiaVidro } from "../services/vidroService";
import { useNavigation } from "contexts/notificationsContext";
import { CampoNomeArquivo } from "../../components/CampoNomeArquivo";
import { useMidiaCompartilhada } from "../../hooks/useMidiaCompartilhada";
import { nomeArquivoPadrao } from "../../hooks/useNomeArquivoPadrao";

interface ModalGerenciamentoMidiasProps {
  open: boolean;
  onClose: () => void;
  vidro: Partial<IVidro>;
  onSave: (midias: {
    imagemUrl?: string;
    imagemNomeArquivo?: string;
  }, idAlvo: number) => void;
  onRefreshVidro: () => void;
}

const UploadArea: React.FC<{
  arquivoAtualUrl?: string;
  arquivoAtualNome?: string;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  isLoading: boolean;
  idVidro?: number;
}> = ({
  arquivoAtualUrl,
  arquivoAtualNome,
  onFileSelect,
  onRemove,
  isLoading,
  idVidro,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
    [onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    multiple: false,
    disabled: isLoading || !idVidro,
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        p: 4,
        textAlign: "center",
        border: isDragActive ? "2px dashed #3C90AD" : "1px solid #e0e0e0",
        backgroundColor: isDragActive ? "#f0f8ff" : "#fff",
        cursor: idVidro ? "pointer" : "not-allowed",
        minHeight: 300,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        "&:hover": {
          borderColor: idVidro ? "#3C90AD" : "#e0e0e0",
          backgroundColor: idVidro ? "#f8f8f8" : "#fff",
        },
        position: "relative",
      }}
    >
      <input {...getInputProps()} />
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {!idVidro && (
        <Typography
          variant="caption"
          color="error"
          sx={{ position: "absolute", top: 8, left: 8 }}
        >
          Salve o vidro antes de gerenciar mídias.
        </Typography>
      )}
      {arquivoAtualUrl ? (
        <Box>
          <img
            src={arquivoAtualUrl}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: 200,
              objectFit: "contain",
              marginBottom: 16,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {arquivoAtualNome || "Arquivo carregado"}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Arraste outro arquivo ou clique para substituir
          </Typography>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#f44336",
              backgroundColor: "rgba(255,255,255,0.7)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ) : (
        <Box>
          <CloudUploadIcon sx={{ fontSize: 48, color: "#999", mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Arraste e solte o arquivo aqui dentro ou
          </Typography>
          <Typography variant="body1" color="text.secondary">
            clique aqui para abrir a janela de seleção
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export const ModalGerenciamentoMidias: React.FC<ModalGerenciamentoMidiasProps> = ({
  open,
  onClose,
  vidro,
  onSave,
  onRefreshVidro,
}) => {
  const { openNotification } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);

  const [imagem, setImagem] = useState<{ url?: string; nome?: string; file?: File }>({
    url: vidro.imagemUrl,
    nome: vidro.imagemNomeArquivo,
  });

  // Etapa D: avisa quantos outros produtos usam este mesmo nome de arquivo.
  const produtosCompartilhando = useMidiaCompartilhada(
    open, "vidros", 2, imagem.nome, vidro.idVidro
  );

  // Reporte de 31/08 (item 6): nome com que o modal ABRIU — a comparação de "nome alterado"
  // é contra ele, não contra o cadastro (a Etapa C já sugere <código>.PNG em produto novo).
  const nomeInicial = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (open) {
      // Etapa C: em cadastro NOVO sem nome informado, sugere <código>.PNG.
      const nome = nomeArquivoPadrao(vidro.codigo, ".PNG", !vidro.dataCriacao, vidro.imagemNomeArquivo);
      nomeInicial.current = nome;
      setImagem({ url: vidro.imagemUrl, nome });
    }
  }, [open, vidro]);

  const handleFileSelect = (file: File) => {
    const extensoesPermitidas = ['.png', '.jpg', '.jpeg'];
    const extensao = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!extensoesPermitidas.includes(extensao)) {
      openNotification({
        message: "Formato inválido. Aceitamos: .PNG, .JPG, .JPEG",
        type: "error",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      openNotification({
        message: "Arquivo muito grande. Tamanho máximo: 10MB",
        type: "error",
      });
      return;
    }

    const url = URL.createObjectURL(file);
    setImagem({ url, nome: file.name, file });
  };

  const handleRemoveFile = async () => {
    if (!vidro.idVidro) {
      openNotification({
        message: "Salve o vidro antes de remover mídias.",
        type: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      const retorno = await removerImagem(vidro.idVidro);
      if (retorno?.sucesso) {
        openNotification({ message: "Imagem removida com sucesso!", type: "success" });
        setImagem({ url: undefined, nome: undefined, file: undefined });
        // Plano 21/09/2026 (Frente 3): vidro Disponível → a remoção foi para a cópia de trabalho;
        // a página navega para ela em vez de recarregar o publicado.
        if (retorno.id === vidro.idVidro) onRefreshVidro();
        else onSave({ imagemUrl: undefined, imagemNomeArquivo: undefined }, retorno.id);
      } else {
        openNotification({ message: "Erro ao remover imagem.", type: "error" });
      }
    } catch (error) {
      console.error("Erro ao remover imagem:", error);
      openNotification({ message: "Erro ao remover imagem.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSalvar = async () => {
    if (!vidro.idVidro) {
      openNotification({
        message: "Salve o vidro antes de gerenciar mídias.",
        type: "warning",
      });
      return;
    }

    // Reporte de 31/08 (item 6): sem arquivo novo, mas com o NOME alterado, grava só o nome —
    // é como o produto assume uma mídia que já está no acervo da empresa (F05A-P3b).
    if (!imagem.file) {
      if (imagem.nome === nomeInicial.current || !imagem.nome?.trim()) {
        onClose();
        return;
      }

      setIsLoading(true);
      try {
        const resultado = await definirNomeMidiaVidro(vidro.idVidro!, imagem.nome);
        if (!resultado) return;

        const idAlvo = resultado.id ?? vidro.idVidro!;
        onSave({ imagemUrl: resultado.url, imagemNomeArquivo: resultado.nomeArquivo }, idAlvo);
        if (idAlvo === vidro.idVidro) onRefreshVidro();
        openNotification(
          resultado.arquivoExiste
            ? { message: "Nome do arquivo salvo com sucesso!", type: "success" }
            : {
                message:
                  `Nome gravado, mas não há arquivo "${resultado.nomeArquivo}" no acervo da empresa. ` +
                  "A imagem só aparecerá quando o arquivo for enviado.",
                type: "warning",
              }
        );
        onClose();
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Etapa D (F05A-P3b): arquivo compartilhado altera a mídia de TODOS os produtos que o usam.
    if (produtosCompartilhando > 0) {
      const segue = window.confirm(
        `O arquivo "${imagem.nome}" é usado por ${produtosCompartilhando} outro(s) produto(s). ` +
        `Substituí-lo altera a imagem de todos eles. Deseja continuar?`
      );
      if (!segue) return;
    }

    setIsLoading(true);
    try {
      const retorno = await uploadImagem(vidro.idVidro!, imagem.file, imagem.nome);
      if (retorno) {
        openNotification({ message: "Imagem salva com sucesso!", type: "success" });
        onSave({
          imagemUrl: retorno.url,
          imagemNomeArquivo: imagem.nome,
        }, retorno.id);
        if (retorno.id === vidro.idVidro) onRefreshVidro();
        onClose();
      } else {
        openNotification({ message: "Erro ao fazer upload da imagem.", type: "error" });
      }
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      openNotification({ message: "Erro ao fazer upload da imagem.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: "700px",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ color: "#3C90AD", fontWeight: 500, fontSize: "1rem" }}>
            Gerenciamento de Mídias
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            disabled={isLoading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <UploadArea
          arquivoAtualUrl={imagem.url}
          arquivoAtualNome={imagem.nome}
          onFileSelect={handleFileSelect}
          onRemove={handleRemoveFile}
          isLoading={isLoading}
          idVidro={vidro.idVidro}
        />
        {/* Ajustes 25/08 (item 12 do docx de 22/08): é este nome que o servidor grava. */}
        <CampoNomeArquivo
          valor={imagem.nome || ""}
          onChange={(nv) => setImagem((a) => ({ ...a, nome: nv }))}
          extensoes=".PNG, .JPG, .JPEG"
          desabilitado={isLoading || !vidro.idVidro}
          produtosCompartilhando={produtosCompartilhando}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={isLoading} sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}>
          CANCELAR
        </Button>
        <Button
          onClick={handleSalvar}
          variant="contained"
          disabled={isLoading || (!imagem.file && imagem.nome === nomeInicial.current)}
          startIcon={<SaveIcon />}
          sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
        >
          SALVAR
        </Button>
      </DialogActions>
    </Dialog>
  );
};
